import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { CATEGORY_META, CATEGORY_KEYS } from "@/lib/categories";
import { ArrowRight } from "lucide-react";

const IMG = {
  market: "https://images.unsplash.com/photo-1783231607347-8b7ab4525a17?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  medieval_market: "https://images.unsplash.com/photo-1783231607347-8b7ab4525a17?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  viking_market: "https://images.unsplash.com/photo-1613477757024-fb6d3fd3c0a9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  pagan_festival: "https://images.unsplash.com/photo-1569430251091-da3b108cff29?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  pagan_event: "https://images.unsplash.com/photo-1768063679495-247e89d5e14d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  reenactment: "https://images.unsplash.com/photo-1711523645098-838ccc182d80?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  concert: "https://images.unsplash.com/photo-1600779547877-be592ef5aad3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
};

export default function Categories() {
  const { t, tCategory } = useLang();
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });

  return (
    <>
      <Seo title={t("nav_categories")} description={t("hero_subtitle")} canonicalPath="/categories" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="mb-10 border-b border-amber-900/30 pb-5">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2">swiss-pagan.ch</div>
          <h1 className="font-serif text-4xl sm:text-5xl uppercase tracking-tight text-amber-100">{t("section_categories")}</h1>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(categories.length ? categories : CATEGORY_KEYS.map((k) => ({ key: k, count: 0 }))).map((c, i) => {
            const meta = CATEGORY_META[c.key] || {};
            const Icon = meta.icon;
            return (
              <Link key={c.key} to={`/category/${c.key}`} data-testid={`category-tile-${c.key}`}
                className="group relative h-64 overflow-hidden rounded-xl border border-[#272A34] hover:border-amber-500/50 transition-all duration-300 hover:-translate-y-1 fade-up"
                style={{ animationDelay: `${i * 60}ms` }}>
                <img src={IMG[c.key]} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(11,12,14,0.96), rgba(11,12,14,0.3) 70%)" }} />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  {Icon && <Icon className="w-8 h-8 mb-3" style={{ color: meta.color }} />}
                  <h2 className="font-serif text-2xl text-amber-100 group-hover:text-amber-50 leading-tight">{tCategory(c.key)}</h2>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="font-mono text-xs text-stone-400">{c.count} {t("results_count")}</span>
                    <ArrowRight className="w-5 h-5 text-amber-500 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}
